/**
 * Types for the Move attachments pipeline (the `moveAttachments`
 * DynamoDB table).
 *
 * A run copies one agency's attachments from a source export in
 * `migration-sourcedatabases` into `prod-atlas-uploads-465358817960`. The
 * source export is left in place. The first task is always a dry run; an
 * engineer confirms the inventory before a second task copies anything.
 *
 * Each source arrives in its own layout, so each one gets its own procedure.
 * Only the systems in MOVE_ATTACHMENTS_READY_SOURCES can run. Vincere lands
 * flat in all_files/ and, when asked, all_images/. JobAdder keeps the YYYYMM
 * folders and uses the same extension split, under all_files/ and all_images/.
 *
 * Copies are server-side. A Vincere destination that already has objects
 * cannot be copied again; an admin has to delete that prefix first. JobAdder
 * skips a key that is already at the destination, so a stopped run can be
 * repeated.
 */
import { SOURCE_SYSTEMS, SOURCE_SYSTEM_LABELS } from './constants.js';
/** Same grant as Redshift export: this writes into that pipeline's import area. */
export const MOVE_ATTACHMENTS_APP_SLUG = 'redshift-export';
export const MOVE_ATTACHMENTS_SOURCE_BUCKET = 'migration-sourcedatabases';
export const MOVE_ATTACHMENTS_DEST_BUCKET = 'prod-atlas-uploads-465358817960';
/** Document files land in this folder under the source and firm. */
export const MOVE_ATTACHMENTS_DEST_FOLDER = 'all_files';
/** Images land beside the documents, under the same source and firm. */
export const MOVE_ATTACHMENTS_IMAGE_FOLDER = 'all_images';
/**
 * Bullhorn is different: the source is encrypted .7z archives, not an
 * already-unzipped S3 tree. A first "extract" phase downloads the archives,
 * unzips them with 7-Zip, and writes the tree back under the source firm's
 * `files/unzipped/`. After that it flattens like any other source — but into
 * two destinations, keeping the nested `<id>/<YYYY.MM>/` layout:
 *   UserFiles/UserWorkFiles/            → people/
 *   UserFiles/UserAttachments/ClientCorporationFiles/ → companies/
 */
export const MOVE_ATTACHMENTS_PEOPLE_FOLDER = 'people';
export const MOVE_ATTACHMENTS_COMPANIES_FOLDER = 'companies';
export const MOVE_ATTACHMENTS_CERTIFICATIONS_FOLDER = 'certifications';
/** Where the extract phase writes the unzipped tree, under `<firm>/files/`. */
export const MOVE_ATTACHMENTS_BULLHORN_UNZIPPED_SUBDIR = 'unzipped';
/**
 * Bullhorn ships many archives per category, in two naming styles that coexist:
 *   Oct_<Firm>_<n>_BULLHORN<n>_UserWorkFiles.7z            (one per category)
 *   backup_id<n>_pUserFiles_UserWorkFiles_<n>_<YYYY.MM>.7z  (monthly chunks)
 * so the category is matched as a case-insensitive substring of the archive
 * name, not an end-anchored pattern. The base `..._BULLHORN<n>.7z` (the TDE
 * database backup) and the `.bak` under `decrypted/` match nothing and are
 * skipped.
 */
export function moveAttachmentsBullhornCategory(archiveName) {
    const name = archiveName.toLowerCase();
    if (!name.endsWith('.7z'))
        return null;
    if (name.includes('userworkfiles'))
        return 'people';
    if (name.includes('clientcorporationfiles'))
        return 'companies';
    if (name.includes('certificationfileattachment'))
        return 'certifications';
    return null;
}
/**
 * Maps an extracted folder name to its category. The internal layout varies by
 * naming style: the per-category archives extract with the category folder at
 * the top (`UserWorkFiles/<id>/<YYYY.MM>/...`), while the monthly chunks nest it
 * under `UserFiles[ 2]/[UserAttachments/]<Category>/...`. The worker walks the
 * unzipped tree and matches each category by the folder's own name, so it finds
 * the root wherever it sits (any depth, numbered `UserFiles 2`, etc.).
 */
export function moveAttachmentsBullhornCategoryForFolder(folderName) {
    switch (folderName.toLowerCase()) {
        case 'userworkfiles':
            return 'people';
        case 'clientcorporationfiles':
            return 'companies';
        case 'certificationfileattachment':
            return 'certifications';
        default:
            return null;
    }
}
/**
 * Source systems whose export layout this pipeline can flatten. The UI lists
 * every source; the ones absent here stay visible and cannot be started.
 */
export const MOVE_ATTACHMENTS_READY_SOURCES = ['vincere', 'jobadder', 'bullhorn', 'thrive'];
/**
 * Document extensions copied into all_files/ by default (Resume + Other file
 * types the Atlas UI accepts, plus .txt from the original keep list). Names
 * with no extension are copied too. Engineers can change the set after the
 * dry-run inventory.
 */
export const MOVE_ATTACHMENTS_KEEP_EXT = [
    '.pdf',
    '.doc',
    '.docx',
    '.odt',
    '.xls',
    '.xlsx',
    '.ppt',
    '.pptx',
    '.csv',
    '.txt',
];
/** Image extensions copied into all_images/ when that option is selected. */
export const MOVE_ATTACHMENTS_IMAGE_EXT = ['.jpg', '.jpeg', '.png', '.heic', '.heif'];
/**
 * Bullhorn copies documents only (no images) into both people/ and companies/.
 * These are the defaults pre-checked after the dry run; engineers can change them.
 */
export const MOVE_ATTACHMENTS_BULLHORN_KEEP_EXT = [
    '.pdf',
    '.doc',
    '.docx',
    '.rtf',
    '.txt',
    '.xls',
    '.xlsx',
    '.pptx',
];
/** Default document keep-ext for a source's dry-run inventory. */
export function moveAttachmentsDefaultKeepExt(sourceSystem) {
    return sourceSystem === 'bullhorn'
        ? MOVE_ATTACHMENTS_BULLHORN_KEEP_EXT
        : MOVE_ATTACHMENTS_KEEP_EXT;
}
/** Label the inventory uses for objects whose destination name has no extension. */
export const MOVE_ATTACHMENTS_NO_EXT_LABEL = '(none)';
/** Concurrent server-side copies. Matches the script's own default. */
export const MOVE_ATTACHMENTS_WORKERS = 40;
const FIRM_RE = /^[a-z0-9][a-z0-9_-]{0,62}$/;
/**
 * Whether a run has copied files to the destination — true once a copy has
 * verified (status `complete`) or any destination result recorded objects.
 * Used to label delete logs as "after copy" vs "dry run only".
 */
export function moveAttachmentsWasCopied(job) {
    if (job.status === 'complete')
        return true;
    return [job.result, job.imageResult, job.certResult].some((result) => result != null && (result.copied > 0 || result.destObjects > 0));
}
const IMAGE_EXT_SET = new Set(MOVE_ATTACHMENTS_IMAGE_EXT);
/**
 * Splits the extensions an engineer checked after the dry run into the
 * document and image keep lists. Image types always go to all_images/ when
 * images were requested; everything else goes to all_files/.
 */
export function partitionMoveAttachmentsExtensions(selectedExts, options) {
    const keepNoExt = selectedExts.includes(MOVE_ATTACHMENTS_NO_EXT_LABEL);
    const rest = selectedExts.filter((ext) => ext !== MOVE_ATTACHMENTS_NO_EXT_LABEL);
    if (options.imagesOnly) {
        return { keepExt: [], keepImageExt: [...rest], keepNoExt: false };
    }
    if (!options.includeImages) {
        return { keepExt: [...rest], keepImageExt: [], keepNoExt };
    }
    const keepImageExt = rest.filter((ext) => IMAGE_EXT_SET.has(ext.toLowerCase()));
    const keepExt = rest.filter((ext) => !IMAGE_EXT_SET.has(ext.toLowerCase()));
    return { keepExt, keepImageExt, keepNoExt };
}
export function isMoveAttachmentsReady(sourceSystem) {
    return MOVE_ATTACHMENTS_READY_SOURCES.includes(sourceSystem);
}
/**
 * Sources whose copy keeps the source folder structure and skips keys already
 * at the destination, so a finished run can be re-copied with a different
 * document/image scope without a new dry run. Vincere lands flat and refuses a
 * destination that already has objects; Bullhorn's scope is fixed — neither
 * qualifies.
 */
export function moveAttachmentsSupportsRecopy(sourceSystem) {
    return sourceSystem === 'jobadder' || sourceSystem === 'thrive';
}
export function moveAttachmentsSourceError(sourceSystem) {
    const value = sourceSystem.trim();
    if (!value)
        return 'Choose a source system.';
    if (!SOURCE_SYSTEMS.includes(value))
        return 'Unknown source system.';
    if (!isMoveAttachmentsReady(value)) {
        const label = SOURCE_SYSTEM_LABELS[value] ?? value;
        return `${label} is not set up yet. Its export uses a different layout.`;
    }
    return null;
}
export function moveAttachmentsFirmError(firm) {
    const value = firm.trim();
    if (!value)
        return 'Enter the agency folder name.';
    if (!FIRM_RE.test(value)) {
        return 'Use the S3 folder name: lowercase letters, numbers, _ and -.';
    }
    return null;
}
export function moveAttachmentsSourcePathError(sourcePath) {
    const value = sourcePath.trim().replace(/\/+$/, '');
    if (!value)
        return 'Enter the source path.';
    const match = value.match(/^(?:s3:\/\/([^/]+)\/)?([a-z0-9_-]+)\/([a-z0-9][a-z0-9_-]*)\/files$/);
    if (!match)
        return 'Use a path like s3://migration-sourcedatabases/vincere/ec3/files.';
    if (match[1] && match[1] !== MOVE_ATTACHMENTS_SOURCE_BUCKET) {
        return `The source bucket must be ${MOVE_ATTACHMENTS_SOURCE_BUCKET}.`;
    }
    return moveAttachmentsSourceError(match[2] ?? '');
}
/**
 * Reads `s3://migration-sourcedatabases/<source>/<agency>/files`. The agency
 * is the folder between the source system and `files`. The date folders under
 * `export_data/` are found later, not typed here.
 */
export function parseMoveAttachmentsSource(sourcePath) {
    const value = sourcePath.trim().replace(/\/+$/, '');
    const match = value.match(/^(?:s3:\/\/([^/]+)\/)?([a-z0-9_-]+)\/([a-z0-9][a-z0-9_-]*)\/files$/);
    if (!match)
        return null;
    const bucket = match[1];
    const sourceSystem = match[2];
    const firm = match[3];
    if (bucket && bucket !== MOVE_ATTACHMENTS_SOURCE_BUCKET)
        return null;
    if (!sourceSystem || !firm)
        return null;
    if (moveAttachmentsSourceError(sourceSystem) || moveAttachmentsFirmError(firm))
        return null;
    return { sourceSystem, firm };
}
/**
 * Source files folder and the two destinations for one agency.
 *
 * For most sources the two destinations are `all_files/` (documents) and
 * `all_images/` (images). For Bullhorn they are `people/` and `companies/`,
 * both documents — the image slot is reused for the companies tree.
 * Returns null when the path is not a ready source. Callers must use this
 * rather than a prefix typed past the files folder.
 */
export function buildMoveAttachmentsPaths(sourcePath) {
    const parsed = parseMoveAttachmentsSource(sourcePath);
    if (!parsed)
        return null;
    const sourcePrefix = `${parsed.sourceSystem}/${parsed.firm}/files/`;
    const base = `000-import/${parsed.sourceSystem}/${parsed.firm}`;
    const isBullhorn = parsed.sourceSystem === 'bullhorn';
    const [docFolder, imageFolder] = isBullhorn
        ? [MOVE_ATTACHMENTS_PEOPLE_FOLDER, MOVE_ATTACHMENTS_COMPANIES_FOLDER]
        : [MOVE_ATTACHMENTS_DEST_FOLDER, MOVE_ATTACHMENTS_IMAGE_FOLDER];
    return {
        sourceBucket: MOVE_ATTACHMENTS_SOURCE_BUCKET,
        sourcePrefix,
        destBucket: MOVE_ATTACHMENTS_DEST_BUCKET,
        destPrefix: `${base}/${docFolder}/`,
        imageDestPrefix: `${base}/${imageFolder}/`,
        certDestPrefix: isBullhorn ? `${base}/${MOVE_ATTACHMENTS_CERTIFICATIONS_FOLDER}/` : null,
    };
}
//# sourceMappingURL=moveAttachments.js.map